import { Link } from "@tanstack/react-router";
import { CalendarDays, Heart, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import {
  formatJainMonth,
  formatJainTithi,
  formatPaksha,
} from "@project/lib/jain-panchang-utils";
import type { JainPanchang } from "@project/types/jain-panchang";

type PanchangDetailDialogProps = {
  entry: JainPanchang | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels: {
    subtitle: string;
    lunarCalendar: string;
    monthPaksha: string;
    significance: string;
    recommended: string;
    goBack: string;
    donate: string;
  };
};

function formatHeadingDate(dateStr: string) {
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function PanchangDetailDialog({
  entry,
  open,
  onOpenChange,
  labels,
}: PanchangDetailDialogProps) {
  if (!entry) {
    return null;
  }

  const monthLabel = formatJainMonth(entry.jain_month);
  const pakshaLabel = formatPaksha(entry.paksha);
  const tithiLabel = formatJainTithi(entry.jain_tithi);
  const occasion =
    entry.religious_occasion ?? entry.festival_name ?? tithiLabel ?? "—";
  const isHighlighted =
    entry.is_important || entry.highlight_in_calendar || entry.festival;
  const EventIcon = isHighlighted ? Sparkles : CalendarDays;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto overflow-x-hidden rounded-3xl border-border p-0">
        <DialogTitle className="sr-only">
          {formatHeadingDate(entry.english_date)}
        </DialogTitle>

        <div className="overflow-x-hidden p-6 pb-4">
          {/* <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {labels.subtitle}
          </p> */}
          <h2 className="mt-2 break-words pr-8 font-serif text-2xl leading-tight text-foreground">
            {formatHeadingDate(entry.english_date)}
          </h2>
        </div>

        <div className="space-y-5 overflow-x-hidden px-6 pb-6">
          <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-4 shadow-sm">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">
                {labels.lunarCalendar}
              </p>
              <p className="mt-2 break-words text-base font-semibold text-foreground">
                {monthLabel} {labels.monthPaksha} — {pakshaLabel} Paksha
              </p>
              <p className="mt-1 break-words text-sm font-medium text-gold">
                {occasion}
              </p>
            </div>
            <div className="bg-gold/15 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-gold">
              <EventIcon className="h-5 w-5" />
            </div>
          </div>

          {entry.significance ? (
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {labels.significance}
              </p>
              <div className="bg-muted/40 mt-2 break-words rounded-2xl border border-border p-4 text-sm leading-relaxed text-foreground">
                {entry.significance}
              </div>
            </div>
          ) : null}

          {entry.recommended_activities ? (
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {labels.recommended}
              </p>
              <div className="bg-gold/5 border-gold/15 mt-2 break-words rounded-2xl border p-4 text-sm leading-relaxed text-foreground">
                ✨ {entry.recommended_activities}
              </div>
            </div>
          ) : null}

          {entry.short_description ? (
            <p className="break-words text-sm leading-relaxed text-muted-foreground">
              {entry.short_description}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-3 overflow-x-hidden border-t border-border p-6 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
          >
            {labels.goBack}
          </Button>
          <Button
            asChild
            className="hover:bg-gold/90 rounded-full bg-gold px-6 text-gold-foreground"
          >
            <Link
              to="/donate"
              className="inline-flex items-center justify-center gap-2"
            >
              <Heart className="h-4 w-4 shrink-0" />
              {labels.donate}
            </Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
